/* eslint-disable @typescript-eslint/naming-convention */
import React, { type FunctionComponent, Fragment, useEffect } from 'react'
import qs from 'query-string'
import { useParams } from 'react-router'
import moment from 'moment'
import { useHistory } from 'react-router-dom'
import { LoadingDots } from '@retailer-platform/shared-components'
import { type DashRouteComponentProps } from 'legacy/dash-components/dash-route/utils/dashRoute.types'
import DocumentTitle from 'legacy/components/DocumentTitle'
import { type PartnerScopedRouteParams } from 'legacy/common/types'
import Shifts from 'domains/shifts/components/Shifts'
import { type ShiftsRouteQuery } from 'domains/shifts/components/types'
import { useDomainAccessControl } from 'domains/shifts/utils/domain/accessControl'
import { Flipper } from 'domains/shifts/access-control/flippers'
import { RecommendedShiftsContextProvider } from 'domains/shifts/components/contexts/RecommendationShiftsContext'
import { useWarehouseContext } from 'utils/contexts/warehouse/WarehouseContext.hooks'
import { ShiftsStateContextProvider } from 'domains/shifts/components/contexts/ShiftsStateContext'
import { useValidWarehouseLocations } from 'domains/shifts/utils/hooks/useValidWarehouseLocations.hook'
import { useFetchCurrentEnterpriseAccountQuery } from '__codegen__/api'
interface Props
  extends DashRouteComponentProps<{
    /*
    Add here any properties that you expect to come from the route. For instance,

    id: string
  */
  }> {}

export const ShiftsListPage: FunctionComponent<Props> = ({ ...props }) => {
  const warehouseContext = useWarehouseContext()
  const validLocations = useValidWarehouseLocations(warehouseContext)
  const { replace } = useHistory()
  const query = qs.parse(location.search) as ShiftsRouteQuery
  const { date, warehouse_location_id } = query

  useEffect(() => {
    if (!warehouse_location_id || !date) {
      const firstWarehouse = validLocations[0]

      const queryString = {
        warehouse_location_id: firstWarehouse.id,
        date: moment().format('YYYY-MM-DD'),
      }
      replace(`${location.pathname}?${qs.stringify(queryString)}`)
    }
  }, [date, replace, validLocations, warehouse_location_id])

  const { partner_id: partnerId, warehouse_id: warehouseId } = useParams<PartnerScopedRouteParams>()
  const hasAccess = useDomainAccessControl()
  const { data: userAccountData, loading: userAccountLoading } =
    useFetchCurrentEnterpriseAccountQuery({
      variables: {},
    })

  const flipper = {
    ultrafastCrossDayDeliveryEnabled: hasAccess([
      {
        flippers: Flipper.ultrafastCrossDayDeliveryEnabled,
      },
      {
        warehouseFlippers: Flipper.ultrafastCrossDayDeliveryEnabled,
      },
    ]),
    minimumStaffedHoursEnabled: hasAccess([
      {
        flippers: Flipper.ShiftManagmentMinStaffedHours,
      },
      {
        warehouseFlippers: Flipper.ShiftManagmentMinStaffedHours,
      },
    ]),
    WarehouseLocationPickupHourEnabled: hasAccess([
      {
        warehouseFlippers: Flipper.WarehouseLocationPickupHourEnabled,
      },
    ]),
  }

  if (!warehouse_location_id || !date || !userAccountData || userAccountLoading)
    return <LoadingDots height={12} />
  const { id: userAccountId } = userAccountData.currentEnterpriseAccount
  return (
    <Fragment>
      <DocumentTitle titleId="shiftsDomain.title" />
      <RecommendedShiftsContextProvider warehouseIdAndDate={props.location.search}>
        <ShiftsStateContextProvider>
          <Shifts
            partnerId={partnerId}
            warehouseId={warehouseId}
            query={query}
            flipper={flipper}
            userAccountId={userAccountId}
            {...props}
          />
        </ShiftsStateContextProvider>
      </RecommendedShiftsContextProvider>
    </Fragment>
  )
}
/* eslint-enable @typescript-eslint/naming-convention */
